// Canonical subtitle-style settings model — shared by the settings page,
// the editor preview, and (mirrored in backend/lib/caption-settings.js) the
// libass renderer.
//
// There are TWO rendered videos per clip, each with INDEPENDENT style:
//   • Video 1 — "ES + EN": bilingual. Spanish primary line (`es`) on top,
//     English translation line (`en`) below (usually dimmed/italic).
//   • Video 2 — "RU": single Russian line (`ru`).
//
// The stored payload (`reels_settings.payload`) historically was a flat object
// (fontFamily/fontSizeEn/layouts…). `normalizeSettings` upgrades any legacy
// payload to the v2 shape below so all three consumers read the same thing.

export const FALLBACK_FONT = 'CormorantGaramond-Bold';

function lineDefaults(overrides = {}) {
  return {
    fontFamily: FALLBACK_FONT,
    fontSize: 64,
    bold: false,
    italic: false,
    primaryColor: '&H00FFFFFF', // white  (ASS &HAABBGGRR)
    outlineColor: '&H00000000', // black
    outlineWidth: 2,
    shadowWidth: 3,
    letterSpacing: 0,
    lineHeight: 1.2,
    posY: 0.79, // vertical anchor, 0..1 of frame height
    align: 'center',          // horizontal: 'left' | 'center' | 'right'
    useBox: false,            // background plate behind the line (ASS BorderStyle 3)
    boxColor: '&H00000000',   // plate colour (alpha taken from boxOpacity)
    boxOpacity: 0.55,         // 0..1 — 1 = fully opaque plate
    ...overrides,
  };
}

/** One-click style presets applied to the whole settings object. */
export const STYLE_PRESETS = [
  {
    id: 'classic',
    label: 'Классика',
    hint: 'Cormorant, белый текст, мягкая тень',
    apply: (s) => ({
      ...s,
      es: { ...s.es, fontFamily: 'CormorantGaramond-Bold', useBox: false, outlineWidth: 2, shadowWidth: 3, primaryColor: '&H00FFFFFF' },
      en: { ...s.en, fontFamily: 'CormorantGaramond-Italic', italic: true, useBox: false, opacity: 0.5 },
      ru: { ...s.ru, fontFamily: 'CormorantGaramond-Bold', useBox: false, outlineWidth: 2, shadowWidth: 3 },
    }),
  },
  {
    id: 'plate',
    label: 'Подложка',
    hint: 'Тёмная плашка под текстом, без обводки',
    apply: (s) => ({
      ...s,
      es: { ...s.es, useBox: true, boxColor: '&H00000000', boxOpacity: 0.55, outlineWidth: 0, shadowWidth: 0 },
      en: { ...s.en, useBox: true, boxColor: '&H00000000', boxOpacity: 0.55, outlineWidth: 0, shadowWidth: 0, opacity: 0.7 },
      ru: { ...s.ru, useBox: true, boxColor: '&H00000000', boxOpacity: 0.55, outlineWidth: 0, shadowWidth: 0 },
    }),
  },
  {
    id: 'bold-outline',
    label: 'Жирная обводка',
    hint: 'Крупный текст, толстая чёрная обводка',
    apply: (s) => ({
      ...s,
      es: { ...s.es, fontSize: 72, bold: true, useBox: false, outlineWidth: 4, shadowWidth: 2 },
      en: { ...s.en, fontSize: 56, useBox: false, outlineWidth: 3, shadowWidth: 1, opacity: 0.6 },
      ru: { ...s.ru, fontSize: 72, bold: true, useBox: false, outlineWidth: 4, shadowWidth: 2 },
    }),
  },
];

export function defaultSettings() {
  return {
    version: 2,
    es: lineDefaults({ posY: 0.79 }),
    en: lineDefaults({
      fontFamily: 'CormorantGaramond-Italic',
      italic: true,
      opacity: 0.5,
      posY: 0.85,
      gap: 0.06, // vertical distance below the ES baseline (fraction of frame height)
    }),
    ru: lineDefaults({ posY: 0.82 }),
  };
}

/**
 * Effective vertical anchor for the EN line. EN is positioned RELATIVE to the
 * ES baseline (es.posY + en.gap) so the ES↔EN distance stays constant whether
 * the Spanish line wraps to one or two lines — and so moving the Spanish line
 * carries the translation with it. Falls back to the legacy absolute en.posY
 * for payloads saved before the gap model.
 */
export function resolveEnPosY(settings) {
  const esPosY = settings?.es?.posY ?? 0.79;
  const gap = settings?.en?.gap != null
    ? settings.en.gap
    : ((settings?.en?.posY ?? 0.85) - esPosY);
  return Math.min(0.99, Math.max(0, esPosY + (Number.isFinite(gap) ? gap : 0.06)));
}

/** Coerce any stored payload (v2 or legacy flat) into the canonical v2 shape. */
export function normalizeSettings(raw) {
  const base = defaultSettings();
  if (!raw || typeof raw !== 'object') return base;

  // Already v2 — merge per line so missing keys fall back to defaults.
  if (raw.version === 2 || (raw.es && raw.ru && typeof raw.es === 'object')) {
    const es = { ...base.es, ...(raw.es || {}) };
    const en = { ...base.en, ...(raw.en || {}) };
    // Preserve the ES↔EN distance for payloads saved before the gap model:
    // derive gap from the stored absolute en.posY if no explicit gap exists.
    if (raw.en && raw.en.gap == null && raw.en.posY != null) {
      en.gap = Number(raw.en.posY) - Number(es.posY);
    }
    return {
      version: 2,
      es,
      en,
      ru: { ...base.ru, ...(raw.ru || {}) },
    };
  }

  // Legacy flat payload → split into es / en / ru lines.
  const layout = raw.layouts?.['9:16'] || {};
  const es = lineDefaults({
    fontFamily: raw.fontFamily ?? base.es.fontFamily,
    fontSize: raw.fontSize ?? base.es.fontSize,
    bold: raw.bold ?? false,
    italic: raw.italic ?? false,
    primaryColor: raw.primaryColor ?? base.es.primaryColor,
    outlineColor: raw.outlineColor ?? base.es.outlineColor,
    outlineWidth: raw.outlineWidth ?? base.es.outlineWidth,
    shadowWidth: raw.shadowWidth ?? base.es.shadowWidth,
    letterSpacing: raw.letterSpacing ?? 0,
    lineHeight: raw.lineHeight ?? base.es.lineHeight,
    posY: layout.line1Y ?? base.es.posY,
  });
  const en = lineDefaults({
    fontFamily: raw.fontFamilyEn ?? raw.fontFamily ?? base.en.fontFamily,
    fontSize: raw.fontSizeEn ?? raw.fontSize ?? base.en.fontSize,
    bold: raw.boldEn ?? false,
    italic: raw.italicEn ?? true,
    primaryColor: raw.primaryColor ?? base.en.primaryColor,
    outlineColor: raw.outlineColor ?? base.en.outlineColor,
    outlineWidth: raw.outlineWidth ?? base.en.outlineWidth,
    shadowWidth: raw.shadowWidth ?? base.en.shadowWidth,
    letterSpacing: raw.letterSpacing ?? 0,
    lineHeight: raw.lineHeight ?? base.en.lineHeight,
    posY: layout.line2Y ?? base.en.posY,
    opacity: raw.opacityEn ?? base.en.opacity,
  });
  const ru = lineDefaults({
    fontFamily: raw.fontFamilyRu ?? raw.fontFamily ?? base.ru.fontFamily,
    fontSize: raw.fontSizeRu ?? raw.fontSize ?? base.ru.fontSize,
    bold: raw.boldRu ?? false,
    italic: raw.italicRu ?? false,
    primaryColor: raw.primaryColor ?? base.ru.primaryColor,
    outlineColor: raw.outlineColor ?? base.ru.outlineColor,
    outlineWidth: raw.outlineWidth ?? base.ru.outlineWidth,
    shadowWidth: raw.shadowWidth ?? base.ru.shadowWidth,
    letterSpacing: raw.letterSpacing ?? 0,
    lineHeight: raw.lineHeight ?? base.ru.lineHeight,
    posY: layout.line1Y ?? base.ru.posY,
  });
  return { version: 2, es, en, ru };
}

/** Parse ASS / CSS colour into BGR bytes (ASS uses &HAABBGGRR, alpha 00 = opaque). */
export function parseAssColor(input) {
  if (input == null || input === '') return null;

  if (typeof input === 'string') {
    const css = input.match(/^#?([0-9a-fA-F]{6})$/i);
    if (css) {
      const rr = css[1].slice(0, 2);
      const gg = css[1].slice(2, 4);
      const bb = css[1].slice(4, 6);
      return { aa: '00', bb, gg, rr };
    }

    const m = input.match(/^&H([0-9A-Fa-f]+)$/i);
    if (m) {
      const digits = m[1];
      if (digits.length < 6) return null;
      const bgr = digits.slice(-6);
      const aa = digits.length >= 8 ? digits.slice(0, digits.length - 6) : '00';
      return {
        aa: aa.slice(-2).padStart(2, '0'),
        bb: bgr.slice(0, 2),
        gg: bgr.slice(2, 4),
        rr: bgr.slice(4, 6),
      };
    }
  }

  return null;
}

/** ASS colour → CSS #rrggbb for pickers and preview. */
export function assToHex(assColor, fallback = '#ffffff') {
  const c = parseAssColor(assColor);
  if (!c) return fallback;
  return `#${c.rr}${c.gg}${c.bb}`.toLowerCase();
}

/** CSS #rrggbb → ASS &HAABBGGRR (opaque by default). */
export function hexToAss(hex, fallback = '&H00FFFFFF') {
  const c = parseAssColor(hex);
  if (!c) return fallback;
  return `&H${c.aa}${c.bb}${c.gg}${c.rr}`.toUpperCase();
}

/** Canonical ASS string for DB / libass (keeps opaque alpha). */
export function normalizeAssColor(input, fallback = '&H00FFFFFF') {
  const c = parseAssColor(input) || parseAssColor(fallback);
  if (!c) return fallback;
  return `&H${c.aa}${c.bb}${c.gg}${c.rr}`.toUpperCase();
}

// Bundled fonts ship as `<Family>-<Variant>.ttf`; libass matches that basename
// but the browser only has the Google "Cormorant Garamond" web font loaded.
// Map the known bundled names to a usable CSS stack so the preview looks right.
export function previewFontFamily(name) {
  if (!name) return "'Cormorant Garamond', serif";
  if (/cormorant/i.test(name)) return "'Cormorant Garamond', 'Newsreader', serif";
  if (/newsreader/i.test(name)) return "'Newsreader', serif";
  if (/manrope/i.test(name)) return "'Manrope', sans-serif";
  return `'${name}', 'Cormorant Garamond', serif`;
}

// Bundled fonts are static cuts (…-Bold / …-Italic / …-Regular). libass selects
// the matching face, so the preview must too — otherwise a `…-Bold` family
// renders at weight 600 in the browser but 700 in the burn-in, which reads as a
// size difference. Derive weight/style from the file variant AND the flags.
export function previewFontVariant(line) {
  const stem = String(line?.fontFamily || '').replace(/\.(ttf|otf)$/i, '');
  const bold = !!line?.bold || /bold/i.test(stem);
  const italic = !!line?.italic || /italic/i.test(stem);
  return {
    fontWeight: bold ? 700 : 400,
    fontStyle: italic ? 'italic' : 'normal',
  };
}

/** CSS text-shadow layers that mimic libass outline (more reliable than -webkit-text-stroke). */
function previewOutlineShadows(line, px) {
  const w = (line.outlineWidth || 0) > 0
    ? Math.max(1, Math.round((line.outlineWidth || 0) * px))
    : 0;
  if (!w) return [];
  const c = assToHex(line.outlineColor, '#000000');
  const layers = [];
  for (let dx = -w; dx <= w; dx += 1) {
    for (let dy = -w; dy <= w; dy += 1) {
      if (dx === 0 && dy === 0) continue;
      if (Math.max(Math.abs(dx), Math.abs(dy)) <= w) {
        layers.push(`${dx}px ${dy}px 0 ${c}`);
      }
    }
  }
  return layers;
}

/** Inline style for the per-line background plate (mirrors ASS BorderStyle 3).
 *  Applied to an inner <span> so it hugs the text and clones across wrapped
 *  lines (box-decoration-break), matching how libass paints the box per line. */
export function previewBoxStyle(line) {
  if (!line?.useBox) return undefined;
  const hex = assToHex(line.boxColor, '#000000');
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const op = line.boxOpacity ?? 0.55;
  return {
    backgroundColor: `rgba(${r}, ${g}, ${b}, ${op})`,
    padding: '0.06em 0.32em',
    borderRadius: '0.14em',
    boxDecorationBreak: 'clone',
    WebkitBoxDecorationBreak: 'clone',
  };
}

/** Build an inline style object for one caption line, scaled to the preview box.
 *  `anchor`: 'bottom' (ES/RU — baseline fixed, wraps grow UP) or 'top' (EN —
 *  top fixed, wraps grow DOWN), mirroring the libass render so the ES↔EN gap is
 *  constant. `posY` overrides the line's own anchor; `align` overrides line.align. */
export function previewLineStyle(line, { boxHeight, opacity, posY, anchor = 'bottom', align } = {}) {
  const px = (boxHeight || 0) / 1920; // px per ASS unit at 1080×1920
  const fontPx = (line.fontSize || 64) * px;
  const shadow = (line.shadowWidth || 0) * px;
  const outlineLayers = previewOutlineShadows(line, px);
  const glow = shadow > 0 ? `0 0 ${shadow * 2}px rgba(0,0,0,0.85)` : null;
  const textShadow = [...outlineLayers, glow].filter(Boolean).join(', ') || 'none';
  // Opacity dims the WHOLE layer — text + outline + shadow + background plate —
  // uniformly (mirrors libass `\alpha`, which sets every alpha channel). CSS
  // element opacity cascades to the inner box <span> too, so the plate fades
  // with it. ES/RU have opacity 1 (no change); only EN exposes the control.
  const layerOpacity = opacity ?? line.opacity ?? 1;
  const anchorY = posY ?? line.posY ?? 0.8;
  const textAlign = align ?? line.align ?? 'center';
  return {
    position: 'absolute',
    left: 0,
    right: 0,
    textAlign,
    // Bottom-anchored: element bottom sits at posY (translateY(-100%)) so it
    // grows up. Top-anchored: element top sits at posY so it grows down.
    top: `${anchorY * 100}%`,
    transform: anchor === 'top' ? 'none' : 'translateY(-100%)',
    color: assToHex(line.primaryColor, '#ffffff'),
    opacity: layerOpacity,
    fontFamily: previewFontFamily(line.fontFamily),
    ...previewFontVariant(line),
    fontSize: `${fontPx}px`,
    lineHeight: line.lineHeight || 1.2,
    letterSpacing: `${(line.letterSpacing || 0) * px}px`,
    textShadow,
    pointerEvents: 'none',
    whiteSpace: 'pre-line',
    padding: '0 6%',
    boxSizing: 'border-box',
    zIndex: 10,
  };
}
